import { useEffect, useState } from "react";
import { Package, Plus, MagnifyingGlass, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";

const ACTIONS = [["purchase", "Purchase"], ["return_from_job", "Return from job"], ["return_supplier", "Return to supplier"], ["damaged", "Damaged/lost"], ["adjustment", "Manual adjustment"]];

export default function Stock() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", brand: "", model_sku: "", category: "", qty_on_hand: 0, avg_cost: 0, retail_price: 0, storage_location: "", low_stock_threshold: 0 });
  const [detail, setDetail] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [adj, setAdj] = useState({ action: "purchase", quantity: 1, unit_cost: 0, notes: "" });

  const load = () => api.get(`/stock${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    await api.post("/stock", { ...form, qty_on_hand: Number(form.qty_on_hand), avg_cost: Number(form.avg_cost), retail_price: Number(form.retail_price), low_stock_threshold: Number(form.low_stock_threshold) });
    setAddOpen(false); setForm({ name: "", brand: "", model_sku: "", category: "", qty_on_hand: 0, avg_cost: 0, retail_price: 0, storage_location: "", low_stock_threshold: 0 });
    toast.success("Stock item added"); load();
  };

  const open = async (item) => { setDetail(item); setLedger((await api.get(`/stock/${item.id}/ledger`)).data); setAdj({ action: "purchase", quantity: 1, unit_cost: item.last_purchase_cost || item.avg_cost || 0, notes: "" }); };
  const doAdjust = async () => {
    const r = (await api.post(`/stock/${detail.id}/adjust`, { ...adj, quantity: Number(adj.quantity), unit_cost: Number(adj.unit_cost) })).data;
    setDetail(r.stock); setLedger((await api.get(`/stock/${detail.id}/ledger`)).data); load();
    toast.success("Ledger updated");
  };

  const low = (i) => i.low_stock_threshold > 0 && i.qty_on_hand <= i.low_stock_threshold;

  return (
    <div className="min-h-screen">
      <header className="px-5 pt-7 pb-3 flex items-center justify-between">
        <h1 className="font-heading text-3xl font-black tracking-tight">Inventory</h1>
        <Button data-testid="new-stock-btn" size="sm" className="h-10 gap-1.5 bg-orange-600 hover:bg-orange-700 font-bold" onClick={() => setAddOpen(true)}><Plus size={18} weight="bold" /> Add</Button>
      </header>
      <div className="px-5 space-y-3">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input data-testid="stock-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search stock" className="h-11 pl-10" />
        </form>
        {list.length === 0 && <div className="text-sm text-zinc-400 py-10 text-center border border-dashed border-zinc-200 rounded-xl" data-testid="stock-empty">No stock items yet.</div>}
        <div className="space-y-2.5 pb-4">
          {list.map((i) => (
            <button key={i.id} data-testid={`stock-row-${i.id}`} onClick={() => open(i)}
              className="w-full text-left bg-white border border-zinc-200 rounded-xl p-3.5 flex items-center gap-3 active:scale-[0.99] transition-transform">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0"><Package size={22} weight="bold" /></div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-zinc-900 truncate">{i.name}</p>
                <p className="text-xs text-zinc-500 truncate">{[i.brand, i.model_sku].filter(Boolean).join(" · ")} avg {money(i.avg_cost)}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-heading font-black text-zinc-900" data-testid={`stock-qty-${i.id}`}>{i.qty_on_hand}</p>
                {low(i) && <span className="text-[9px] font-bold uppercase bg-red-100 text-red-700 px-1.5 py-0.5 rounded flex items-center gap-0.5" data-testid={`low-stock-${i.id}`}><Warning size={10} weight="fill" /> Low</span>}
              </div>
            </button>
          ))}
        </div>
      </div>

      <Drawer open={addOpen} onOpenChange={setAddOpen}>
        <DrawerContent className="max-w-md mx-auto">
          <DrawerHeader><DrawerTitle className="font-heading">New Stock Item</DrawerTitle></DrawerHeader>
          <div className="px-4 pb-2 space-y-2">
            <Input placeholder="Product name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10" data-testid="stock-name" />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="h-10" />
              <Input placeholder="Model/SKU" value={form.model_sku} onChange={(e) => setForm({ ...form, model_sku: e.target.value })} className="h-10" />
              <Input placeholder="Qty on hand" type="number" value={form.qty_on_hand} onChange={(e) => setForm({ ...form, qty_on_hand: e.target.value })} className="h-10" data-testid="stock-qty-input" />
              <Input placeholder="Unit cost" type="number" value={form.avg_cost} onChange={(e) => setForm({ ...form, avg_cost: e.target.value })} className="h-10" data-testid="stock-cost-input" />
              <Input placeholder="Retail ref" type="number" value={form.retail_price} onChange={(e) => setForm({ ...form, retail_price: e.target.value })} className="h-10" />
              <Input placeholder="Low-stock alert below" type="number" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} className="h-10" data-testid="stock-threshold-input" />
            </div>
          </div>
          <DrawerFooter><Button data-testid="create-stock-btn" onClick={create} className="h-12 font-bold bg-orange-600 hover:bg-orange-700">Create</Button></DrawerFooter>
        </DrawerContent>
      </Drawer>

      <Drawer open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DrawerContent className="max-w-md mx-auto">
          <DrawerHeader><DrawerTitle className="font-heading">{detail?.name}</DrawerTitle></DrawerHeader>
          <div className="px-4 pb-4 space-y-3 max-h-[65vh] overflow-y-auto">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-zinc-50 rounded-lg p-2"><p className="font-heading font-black" data-testid="detail-qty">{detail?.qty_on_hand}</p><p className="text-[10px] uppercase text-zinc-400">On hand</p></div>
              <div className="bg-zinc-50 rounded-lg p-2"><p className="font-heading font-black" data-testid="detail-avg">{money(detail?.avg_cost)}</p><p className="text-[10px] uppercase text-zinc-400">Avg cost</p></div>
              <div className="bg-zinc-50 rounded-lg p-2"><p className="font-heading font-black">{money(detail?.last_purchase_cost)}</p><p className="text-[10px] uppercase text-zinc-400">Last buy</p></div>
            </div>
            <div className="border border-zinc-200 rounded-lg p-2.5 space-y-2">
              <p className="text-[10px] uppercase font-bold text-zinc-400">Adjust stock</p>
              <div className="grid grid-cols-2 gap-2">
                <select data-testid="adjust-action" value={adj.action} onChange={(e) => setAdj({ ...adj, action: e.target.value })} className="h-9 border border-zinc-200 rounded-lg px-2 bg-white text-sm">
                  {ACTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <Input placeholder="Qty" type="number" value={adj.quantity} onChange={(e) => setAdj({ ...adj, quantity: e.target.value })} className="h-9" data-testid="adjust-qty" />
                {adj.action === "purchase" && <Input placeholder="Unit cost" type="number" value={adj.unit_cost} onChange={(e) => setAdj({ ...adj, unit_cost: e.target.value })} className="h-9" data-testid="adjust-cost" />}
                <Input placeholder="Notes" value={adj.notes} onChange={(e) => setAdj({ ...adj, notes: e.target.value })} className="h-9" />
              </div>
              <Button size="sm" className="h-9 w-full" data-testid="apply-adjust-btn" onClick={doAdjust}>Apply adjustment</Button>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-zinc-400 mb-1.5">Ledger</p>
              <div className="space-y-1.5" data-testid="stock-ledger">
                {ledger.map((l) => (
                  <div key={l.id} className="flex justify-between text-sm border-b border-zinc-100 pb-1">
                    <span className="text-zinc-600">{(l.created_at || "").slice(0, 10)} · {l.action}{l.related_job_id ? " (job)" : ""}</span>
                    <span className={`font-bold ${l.quantity >= 0 ? "text-green-600" : "text-red-600"}`}>{l.quantity >= 0 ? "+" : ""}{l.quantity} @ {money(l.unit_cost)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DrawerFooter><Button variant="ghost" onClick={() => setDetail(null)}>Close</Button></DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
